/**
 * Turn a sign-in failure into a sentence that names the way out.
 *
 * The two ways in are strict and enforced server-side, so a wrong door is not
 * a detour but an error -- and the raw errors are written for us, not for the
 * person reading them. What the console receives, verbatim:
 *
 *   "PreSignUp failed with error This account is not permitted to sign in
 *    with Google."      -- a non-@aerovect.com Google account tried the
 *                          employee door (platform_auth pre_signup.py)
 *   "No matching state found in storage"
 *   "Invalid state"     -- the callback's state did not match: a stale tab, a
 *                          bookmarked callback URL, or a second login that
 *                          overwrote the first (SW-6866)
 *   "access_denied"     -- the person backed out at the identity provider
 *   "session expired…" / "invalid_grant"
 *                       -- the stored refresh token could not renew the session
 *                          on page load (cognitoAppAuth SESSION_LAPSED), because
 *                          it expired or was revoked
 *
 * Anything unrecognised is shown as-is behind a plain prefix, because hiding
 * an unknown failure behind a friendly sentence is how a real outage becomes
 * "it just says try again".
 */
export const WRONG_DOOR_GOOGLE =
  "That Google account isn't an AeroVect account. External users sign in with email and password below.";

export const STALE_SIGN_IN =
  "That sign-in attempt expired. Please sign in again.";

export const CANCELLED_SIGN_IN =
  "Sign-in was cancelled before it finished. Please sign in again.";

export const SESSION_EXPIRED =
  "Your session has expired. Please sign in again.";

export function describeSignInError(error: Error | string): string {
  const raw = typeof error === "string" ? error : error.message;
  const text = raw.toLowerCase();

  if (
    text.includes("not permitted to sign in with google") ||
    text.includes("federated sign-in from")
  ) {
    return WRONG_DOOR_GOOGLE;
  }
  if (text.includes("no matching state") || text.includes("invalid state")) {
    return STALE_SIGN_IN;
  }
  if (text.includes("access_denied") || text.includes("access denied")) {
    return CANCELLED_SIGN_IN;
  }
  if (
    text.includes("session expired") ||
    text.includes("invalid_grant") ||
    text.includes("refresh token")
  ) {
    return SESSION_EXPIRED;
  }
  return `Sign-in failed: ${raw}`;
}
